import React, { useState } from "react";
import { Autocomplete, TextField, Box, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Product, Variant } from "../types";

// add near the top of the file (below imports)
const formatVariantAttrs = (attrs: any) => {
  if (!Array.isArray(attrs) || attrs.length === 0) return "";

  return attrs
    .map((attr) => {
      const name = attr?.name ?? "";

      // NEW shape: { name, value }
      if (typeof attr?.value === "string") {
        return `${name}: ${attr.value}`;
      }

      // OLD shape: { name, values: string[] }
      if (Array.isArray(attr?.values)) {
        return `${name}: ${attr.values.filter(Boolean).join(", ")}`;
      }

      return "";
    })
    .filter(Boolean)
    .join(" / ");
};


interface SearchBarProps {
  products: Product[];
  onAddToOrder: (variant: Variant) => void;
  onAddToQuickAdd: (product: Product) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({
  products,
  onAddToOrder,
  onAddToQuickAdd,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const handleAddToQuickAdd = (product: Product) => {
    onAddToQuickAdd(product);
  };

  const handleAddToOrder = (variant: Variant) => {
    onAddToOrder(variant);
  };

  return (
    <Box>
      <Autocomplete
        options={products.flatMap((product) =>
          product.variants.map((variant) => ({
            product,
            variant,
          }))
        )}
        getOptionLabel={({ product, variant }) => {
  const attrsLabel = formatVariantAttrs(variant.attributes);
  return `${product.product_name} - ${attrsLabel || variant.sku}`;
}}

        renderOption={(props, { product, variant }) => (
          <li
            {...props} // Ensure compatibility with `li` elements
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              position: "relative",
              padding: 8,
              listStyle: "none",
            }}
          >
            <img
              src={variant.image_url || product.image_url || ""}
              alt={product.product_name}
              style={{ width: 50, height: 50, objectFit: "contain" }}
            />
            <Box>
              <Typography>
  {`${product.product_name} - ${
    formatVariantAttrs(variant.attributes) || variant.sku
  }`}
</Typography>

              <Typography variant="body2" color="text.secondary">
                SKU: {variant.sku || "No SKU"} | $
                {(variant.price !== null && variant.price !== undefined
                  ? variant.price.toFixed(2)
                  : "N/A")}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation(); // Prevent triggering parent row click
                handleAddToQuickAdd(product);
              }}
              style={{
                position: "absolute",
                right: 10,
                display: "inline-flex",
              }}
            >
              <AddIcon />
            </IconButton>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search for products"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        )}
        onChange={(event, value) =>
          value?.variant && handleAddToOrder(value.variant)
        }
      />
    </Box>
  );
};

export default SearchBar;
